import {
  type SegmentAction,
  type SegmentComponent,
  triggerSegmentAction,
} from '@/src/services/segment'
import { useSardine } from '@/src/services/Sardine'
import { sardineIdKey, type SardineId } from '@/src/services/Sardine/SardineId'
import { type ComponentProps, forwardRef, type LegacyRef } from 'react'
import { Input as BentoInput } from '@/src/components/ui/forms/inputs/Input'

type InputPropsInterface = ComponentProps<typeof BentoInput> & {
  segmentTrack?: boolean
  segmentAction?: SegmentAction
  segmentComponent?: SegmentComponent
  /** @see {@link SardineId} */
  sardineId?: SardineId
}

type InputRef =
  NonNullable<ComponentProps<typeof BentoInput>['ref']> extends LegacyRef<infer T> ? T : never

export const Input = forwardRef<InputRef, InputPropsInterface>(
  (
    { segmentTrack = true, segmentAction, segmentComponent = 'Input', sardineId, ...props },
    ref,
  ) => {
    const { trackTextChange, trackFocusChange } = useSardine()

    const onFocus: ComponentProps<typeof BentoInput>['onFocus'] = (...args) => {
      props.onFocus?.(...args)

      if (segmentTrack && segmentAction) {
        void triggerSegmentAction({ component: segmentComponent, action: segmentAction })
      }
    }

    return (
      <BentoInput
        ref={ref}
        {...props}
        onFocus={onFocus}
        {...(sardineId
          ? {
              onChangeText(...args) {
                props.onChangeText?.(...args)
                void trackTextChange(sardineId, args[0])
              },
              onFocus(...args) {
                onFocus(...args)
                void trackFocusChange(sardineId, true)
              },
              onBlur(...args) {
                props.onBlur?.(...args)
                void trackFocusChange(sardineId, false)
              },
              dataSet: { [sardineIdKey]: sardineId, ...props.dataSet },
            }
          : undefined)}
      />
    )
  },
)

/** @deprecated just use {@link Input} */
export const InputComponent = Input

/** @deprecated just use the named import {@link Input} */
export default InputComponent
